// MIDAS V3 — History filter chip row (Phase 5)
//
// Port of the chip row from history-page.jsx (lines 207-226). Renders:
//   • "Filter" eyebrow on the left
//   • Chip group: All / Platform (manager-only) / per-machine chips
//
// Server-side filter is the existing `?machine_id=` param via
// `setMachineFilter` on `midasHistoryPageSlice`:
//   • All       — machineFilter "all" (no param)
//   • Platform  — machineFilter "platform" (sends ?machine_id=null)
//   • Machine X — machineFilter "<machineId>" (sends ?machine_id=<id>)
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import { HistoryMachineFilter } from "../../../midasHistoryPageSlice";

export interface HistoryMachineChip {
  id: string;
  label: string;
}

interface MidasHistoryFiltersV3Props {
  active: HistoryMachineFilter;
  /** Per-machine chips (one per machine the user has chatted with). */
  machines: HistoryMachineChip[];
  /** Whether the user is a manager — gates the "Platform" chip per spec. */
  showPlatformChip: boolean;
  onChange: (next: HistoryMachineFilter) => void;
  /** Localised "Filter" eyebrow label. */
  filterEyebrow: string;
  /** Localised "Všetky chaty" / "All chats" label. */
  allLabel: string;
  /** Localised "◆ Platforma" / "◆ Platform" label. */
  platformLabel: string;
}

const Chip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
}> = ({ active, onClick, children, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={ariaLabel}
    style={{
      height: 28,
      padding: "0 12px",
      borderRadius: 999,
      fontSize: 12.5,
      fontFamily: "inherit",
      cursor: "pointer",
      background: active ? "var(--midas-v3-ink)" : "var(--midas-v3-card)",
      color: active ? "var(--midas-v3-bg)" : "var(--midas-v3-ink-2)",
      border: active
        ? "1px solid var(--midas-v3-ink)"
        : "1px solid var(--midas-v3-border)",
      transition: "background-color .12s ease, color .12s ease, border-color .12s ease",
      whiteSpace: "nowrap",
      maxWidth: 200,
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
    onMouseEnter={(e) => {
      if (!active) {
        e.currentTarget.style.background = "var(--midas-v3-card-2)";
      }
    }}
    onMouseLeave={(e) => {
      if (!active) {
        e.currentTarget.style.background = "var(--midas-v3-card)";
      }
    }}
  >
    {children}
  </button>
);

const MidasHistoryFiltersV3: React.FC<MidasHistoryFiltersV3Props> = ({
  active,
  machines,
  showPlatformChip,
  onChange,
  filterEyebrow,
  allLabel,
  platformLabel,
}) => {
  return (
    <>
      <span className="midas-v3-eyebrow" style={{ marginRight: 4 }}>
        {filterEyebrow}
      </span>
      <Chip
        active={active === "all"}
        onClick={() => onChange("all")}
        ariaLabel={allLabel}
      >
        {allLabel}
      </Chip>
      {showPlatformChip && (
        <Chip
          active={active === "platform"}
          onClick={() => onChange("platform")}
          ariaLabel={platformLabel}
        >
          ◆ {platformLabel}
        </Chip>
      )}
      {machines.map((m) => (
        <Chip
          key={m.id}
          active={active === m.id}
          onClick={() => onChange(m.id)}
          ariaLabel={m.label}
        >
          {m.label}
        </Chip>
      ))}
    </>
  );
};

export default MidasHistoryFiltersV3;
